import React, { useState, useEffect } from 'react';
import {getAuthData} from './auth.js';
import {
    fetchObjects,
    fetchMyObjects,
    fetchObject,
    createObject,
    updateObject,
    deleteObject,
} from './api-interactions.js';

export function CrudNotAuthenticated() {
    return (
        <div className="container mt-5">
            <h1>LearningFace.ai Contributor</h1>
            <div id="auth-section">
                <p>You need to authenticate to use this application.</p>
            </div>
        </div>
    );
}


export function CrudUI() {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        activityIDs: [],
        xAPIProfileURL: '',
        inputSelectors: [],
        model: '',
        modelFormat: '', 
        modelTrainingDataSource: '', // New field for training data source
        resultFormat: '',
        license: '',
        creator: {
            person: {
                namePrefix: '',
                givenName: '',
                middleName: '',
                familyName: '',
                nameSuffix: '',
                email: '',
                website: '',
                socialMediaLinks: [],
            },
            sourceOrganization: {
                name: '',
                email: '',
                website: '',
                socialMediaLinks: [],
            },
        },
    });

    const [showForm, setShowForm] = useState(false); // State to control form visibility
    const [modelRows, setModelRows] = useState(1); // State for how many rows in the Model textarea

    // Handle "Create New Object" button click
    const handleCreateNew = () => {
        setFormMode('create');
        setFormData({
            name: '',
            description: '',
            category: '',
            activityIDs: [],
            xAPIProfileURL: '',
            inputSelectors: [],
            model: '',
            modelFormat: '',
            modelTrainingDataSource: '',
            resultFormat: '',
            license: '',
            creator: {
                person: {
                    namePrefix: '',
                    givenName: '',
                    middleName: '',
                    familyName: '',
                    nameSuffix: '',
                    email: '',
                    website: '',
                    socialMediaLinks: [],
                },
                sourceOrganization: {
                    name: '',
                    email: '',
                    website: '',
                    socialMediaLinks: [],
                },
            },
        });
        document.getElementById('dynamicFormContainer').style.display = 'block'; // Show the form
        document.getElementById('dynamicForm').scrollIntoView({ behavior: 'smooth' }); // Scroll to the form
    };

    const handleEditObject = async (objectId) => {
        try {
            console.log('Editing object:', objectId);
            const objectData = await fetchObject(objectId);
    
            // Ensure backward compatibility by adding default values
            const updatedObjectData = {
                ...objectData,
                model: objectData.model || '', // Default to an empty string if `model` is missing
                modelFormat: objectData.modelFormat || '', // Default to an empty string
                modelTrainingDataSource: objectData.modelTrainingDataSource || '', // Default to an empty string
            };
    
            setFormMode('edit');
            setEditObjectId(objectId);
            setFormData(updatedObjectData); // Populate the form with the object's data
            document.getElementById('dynamicFormContainer').style.display = 'block'; // Show the form
            document.getElementById('dynamicForm').scrollIntoView({ behavior: 'smooth' }); // Scroll to the form
        } catch (error) {
            console.error('Error fetching object for editing:', error);
        }
    };

    // Handle "Delete Object" button click
    const handleDeleteObject = async (objectId) => {
        try {
            console.log('Deleting object:', objectId);
            await deleteObject(objectId);
            console.log('Deleted object:', objectId);
            displayObjects(); // Refresh the object list
        } catch (error) {
            console.error('Error deleting object:', error);
        }
    };
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        // Adjust rows for model textarea based on modelFormat selection
        if (name === 'modelFormat') {
            if (value === 'brain.js') {
                setModelRows(10);
            } else {
                setModelRows(1);
            }
        }
    };

    const handleNestedInputChange = (field, subfield, value) => {
        setFormData({
            ...formData,
            [field]: {
                ...formData[field],
                [subfield]: value,
            },
        });
    };

    const handleDynamicFieldChange = (field, index, value) => {
        const updatedField = [...formData[field]];
        updatedField[index] = value;
        setFormData({ ...formData, [field]: updatedField });
    };

    const handleAddDynamicField = (field) => {
        setFormData({ ...formData, [field]: [...(formData[field] || []), ''] });
    };

    const handleRemoveDynamicField = (field, index) => {
        const updatedField = [...formData[field]];
        updatedField.splice(index, 1);
        setFormData({ ...formData, [field]: updatedField });
    };

    const handleNestedDynamicFieldChange = (field, subfield, index, value) => {
        const updatedField = [...formData[field][subfield]];
        updatedField[index] = value;
        setFormData({
            ...formData,
            [field]: {
                ...formData[field],
                [subfield]: updatedField,
            },
        });
    };

    const handleAddNestedDynamicField = (field, subfield) => {
        setFormData({
            ...formData,
            [field]: {
                ...formData[field],
                [subfield]: [...(formData[field][subfield] || []), ''],
            },
        });
    };

    const handleRemoveNestedDynamicField = (field, subfield, index) => {
        const updatedField = [...formData[field][subfield]];
        updatedField.splice(index, 1);
        setFormData({
            ...formData,
            [field]: {
                ...formData[field],
                [subfield]: updatedField,
            },
        });
    };

    const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
    const [editObjectId, setEditObjectId] = useState(null);

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // Validate the Model field based on modelFormat
        const validation = validateModelField(formData.modelFormat, formData.model);
        if (!validation.valid) {
            alert(validation.message);
            return;
        }

        try {
            if (formMode === 'edit') {
                const updatedObject = await updateObject(editObjectId, formData);
                console.log('Updated object:', updatedObject);
            } else {
                const newObject = await createObject(formData);
                console.log('Created new object:', newObject);
            }
            await displayObjects();
        } catch (error) {
            console.error('Error saving object:', error);
        }
    };

    function validateModelField(modelFormat, modelValue) {
        if (modelFormat === 'brain.js') {
            let parsed;
            try {
                parsed = JSON.parse(modelValue);
            } catch {
                return { valid: false, message: 'The Model field must be valid JSON for brain.js format.' };
            }

            // Only allow specific top-level properties
            const allowedProps = ['layers', 'input', 'output', 'options', 'inputSize', 'outputSize', 'inputRange', 'outputRange'];
            const keys = Object.keys(parsed);
            const hasAllowed = keys.some(key => allowedProps.includes(key));
            const hasUnexpected = keys.some(key => !allowedProps.includes(key));

            if (!hasAllowed) {
                return { valid: false, message: 'The Model field does not appear to be a valid brain.js model (missing expected properties).' };
            }
            if (hasUnexpected) {
                return { valid: false, message: 'The Model field contains unexpected properties.' };
            }

            // Optionally, check types of known properties
            // (add more checks as needed for your use case)

            return { valid: true };
        } else {
            try {
                new URL(modelValue);
                return { valid: true };
            } catch {
                return { valid: false, message: 'Please enter a valid URL for the Model field.' };
            }
        }
    }
    
    // Function to display objects in the list
    async function displayObjects() {
        try {
            const objects = await fetchMyObjects();
            const objectList = document.getElementById('objectList');
            objectList.innerHTML = '<h2>My Learning Analytics Models</h2>';

            if (!objects || objects.length === 0) {
                objectList.innerHTML += '<p>No objects stored in Spaces yet.</p>';
                return;
            }

            const listGroup = document.createElement('div');
            listGroup.className = 'list-group';

            objects.forEach(object => {
                const listItem = document.createElement('div');
                listItem.className = 'object-item list-group-item d-flex justify-content-between align-items-center';
                listItem.dataset.objectId = object.key;

                const objectDetails = document.createElement('div');
                objectDetails.innerHTML = `<p><strong>Name:</strong> ${object.name}</p>`;
                objectDetails.innerHTML += `<p><strong>Creator:</strong> ${object.creator.person.namePrefix} ${object.creator.person.givenName} ${object.creator.person.familyName} ${object.creator.person.nameSuffix} ${object.creator.sourceOrganization.name}</p>`;

                const objectActions = document.createElement('div');
                const editButton = document.createElement('button');
                editButton.className = 'btn btn-sm btn-outline-primary mr-2';
                editButton.textContent = 'Edit';
                editButton.addEventListener('click', () => handleEditObject(object.id));

                const deleteButton = document.createElement('button');
                deleteButton.className = 'btn btn-sm btn-outline-danger';
                deleteButton.textContent = 'Delete';
                deleteButton.addEventListener('click', () => handleDeleteObject(object.id));

                objectActions.appendChild(editButton);
                objectActions.appendChild(deleteButton);

                listItem.appendChild(objectDetails);
                listItem.appendChild(objectActions);
                listGroup.appendChild(listItem);

                //Debug
                console.log(object);
            });

            objectList.appendChild(listGroup);

        } catch (error) {
            console.error('Error displaying objects:', error);
            document.getElementById('objectList').innerHTML = `<p class="text-danger">Error loading objects.</p>`;
        } finally {
            document.getElementById('showCreateFormButton').style.display = 'block';
            document.getElementById('dynamicFormContainer').style.display = 'none'; // By default hide the form
        }
    }

    useEffect(() => {
        if (showForm && window.$ && window.$.fn.tooltip) {
            window.$('[data-toggle="tooltip"]').tooltip('dispose');
            window.$('[data-toggle="tooltip"]').tooltip();
        }
    }, [showForm]);

    useEffect(() => {
        const fetchAndDisplayObjects = async () => {
            await displayObjects();
        };
        fetchAndDisplayObjects();
    }, []);

    return (
        <>
        <div className="container mt-4">
            <div id="objectList"></div>
            <button id="showCreateFormButton"
                    className="btn btn-success mt-3"
                    onClick={handleCreateNew}>
                Create New Object
            </button></div>
        <div className="container mt-4">
            <div id="dynamicFormContainer" className="mt-4">
            <h3>Create or Edit Object</h3>
            <form id="dynamicForm" onSubmit={handleFormSubmit}>
                {/* General Info */}
                <div className="form-group">
                    <label htmlFor="name" title="Enter a descriptive name for your model." data-toggle="tooltip">Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="description" title="Describe what your model does, what inputs are expected, and how it was trained." data-toggle="tooltip">Description *</label>
                    <textarea
                        id="description"
                        name="description"
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="category" title="Enter a short category label." data-toggle="tooltip">Category *</label>
                    <input
                        type="text"
                        id="category"
                        name="category"
                        className="form-control"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                {/* License */}
                <div className="form-group">
                    <label htmlFor="license" title="Select a license type." data-toggle="tooltip">License *</label>
                    <select
                        id="license"
                        name="license"
                        className="form-control"
                        value={formData.license}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="">Select a license</option>
                        <option value="https://creativecommons.org/publicdomain/zero/1.0/">
                            https://creativecommons.org/publicdomain/zero/1.0/
                        </option>
                        <option value="https://mit-license.org">https://mit-license.org</option>
                    </select>
                </div>

                {/* Model Format */}
                <div className="form-group">
                    <label htmlFor="modelFormat" title="Select a model format." data-toggle="tooltip">Model Format *</label>
                    <select
                        id="modelFormat"
                        name="modelFormat"
                        className="form-control"
                        value={formData.modelFormat}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="">Select a format</option>
                        <option value="brain.js">brain.js</option>
                        <option value="scikit-learn">scikit-learn</option>
                        <option value="tensorflow.js">tensorflow.js</option>
                    </select>
                </div>

                {/* Model */}
                <div className="form-group">
                    <label htmlFor="model" title="Enter model as brain.js JSON or a link to your model." data-toggle="tooltip">Model</label>
                    <p className="learningface-tag">
                        For brain.js please paste the full text here. For other model types please provide a URL to your openly published model, e.g. for tensorflow.js: https://your-domain.com/models/my-model/model.json
                    </p>
                    <textarea
                        id="model"
                        name="model"
                        className="form-control"
                        rows={modelRows}
                        value={formData.model}
                        onChange={handleInputChange}
                    />
                </div>

                {/* Model Training Data Source */}
                <div className="form-group">
                    <label htmlFor="modelTrainingDataSource" title="Enter a url to your training data if applicable as open access." data-toggle="tooltip">Model Training Data Source</label>
                    <input
                        type="url"
                        id="modelTrainingDataSource"
                        name="modelTrainingDataSource"
                        className="form-control"
                        value={formData.modelTrainingDataSource}
                        onChange={handleInputChange}
                    />
                </div>

                {/* Form Section for Filters */}
                <div className="inferable-form-section">
                    <h5>Experience Data Filters</h5>
                </div>

                {/* Activity IDs */}
                <div className="form-group">
                    <label htmlFor="activityIDs" title="Enter URLs for xAPI Activity IDs to filter learner expereince data on. xAPI Activities are stored in a Learning Record Store are one way of filtering." data-toggle="tooltip">Activity IDs</label><br/>
                    {formData.activityIDs.map((activityID, index) => (
                        <div key={index} className="d-flex align-items-center">
                            <input
                                type="url"
                                className="form-control mr-2"
                                value={activityID}
                                onChange={(e) =>
                                    handleDynamicFieldChange('activityIDs', index, e.target.value)
                                }
                                required
                            />
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveDynamicField('activityIDs', index)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-success btn-sm mt-2"
                        onClick={() => handleAddDynamicField('activityIDs')}
                    >
                        Add Activity ID
                    </button>
                </div>

                {/* xAPI Profile URL */}
                <div className="form-group">
                    <label htmlFor="xAPIProfileURL" title="While xAPI provides a flexible framework for tracking data, profiles add structure and consistency. They define how xAPI should be used within a specific context, ensuring that data is captured in a standardized way such as the expected actions (verbs) that a learner (actor) may take for a kind of learning activity." data-toggle="tooltip">xAPI Profile URL</label>
                    <input
                        type="url"
                        id="xAPIProfileURL"
                        name="xAPIProfileURL"
                        className="form-control"
                        value={formData.xAPIProfileURL}
                        onChange={handleInputChange}
                    />
                </div>

                {/* Inputs */}
                <div className="form-group">
                    <label  title="JSON code patterns expected in the xAPI statement, e.g. context parameters." data-toggle="tooltip">Input Selectors</label><br/>
                    {formData.inputSelectors.map((inputSelector, index) => (
                        <div key={index} className="d-flex align-items-center">
                            <textarea
                                className="form-control mr-2"
                                rows="3"
                                value={inputSelector}
                                onChange={(e) =>
                                    handleDynamicFieldChange('inputSelectors', index, e.target.value)
                                }
                                required
                            />
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveDynamicField('inputSelectors', index)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-success btn-sm mt-2"
                        onClick={() => handleAddDynamicField('inputSelectors')}
                    >
                        Add Input Selector
                    </button>
                </div>
                
                {/* Form Section Label */}
                <div className="inferable-form-section">
                    <h5>Creator (Person)</h5>
                </div>

                {/* Creator (Person) */}
                <div className="form-group">
                    <label htmlFor="namePrefix"  title="Enter a name prefix, e.g. Dr., Mr., Ms." data-toggle="tooltip">Name Prefix</label>
                    <input
                        type="text"
                        id="namePrefix"
                        name="namePrefix"
                        className="form-control"
                        value={formData.creator.person.namePrefix}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                namePrefix: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="givenName" title="Enter a given name (First Name in U.S. and Western Hemisphere)." data-toggle="tooltip">Given Name</label>
                    <input
                        type="text"
                        id="givenName"
                        name="givenName"
                        className="form-control"
                        value={formData.creator.person.givenName}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                givenName: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="middleName" title="Enter a middle name." data-toggle="tooltip">Middle Name</label>
                    <input
                        type="text"
                        id="middleName"
                        name="middleName"
                        className="form-control"
                        value={formData.creator.person.middleName}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                middleName: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="familyName" title="Enter a family name (Last Name in U.S. and Western Hemisphere)." data-toggle="tooltip">Family Name</label>
                    <input
                        type="text"
                        id="familyName"
                        name="familyName"
                        className="form-control"
                        value={formData.creator.person.familyName}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                familyName: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="nameSuffix" title="Enter a name suffix, e.g. Jr., Sr." data-toggle="tooltip">Name Suffix</label>
                    <input
                        type="text"
                        id="nameSuffix"
                        name="nameSuffix"
                        className="form-control"
                        value={formData.creator.person.nameSuffix}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                nameSuffix: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="email" title="Enter an email address." data-toggle="tooltip">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        className="form-control"
                        value={formData.creator.person.email}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                email: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="website" title="Enter a web site address." data-toggle="tooltip">Website</label>
                    <input
                        type="url"
                        id="website"
                        name="website"
                        className="form-control"
                        value={formData.creator.person.website}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'person', {
                                ...formData.creator.person,
                                website: e.target.value,
                            })
                        }
                    />

                    {/* Social Media Links for Person */}
                    <label title="Enter 0 or more social media links." data-toggle="tooltip">Social Media Links (Person)</label>
                    {formData.creator.person.socialMediaLinks.map((link, index) => (
                        <div key={index} className="d-flex align-items-center">
                            <input
                                type="url"
                                className="form-control mr-2"
                                value={link}
                                onChange={(e) =>
                                    handleNestedDynamicFieldChange('creator', 'person', 'socialMediaLinks', index, e.target.value)
                                }
                            />
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() =>
                                    handleRemoveNestedDynamicField('creator', 'person', 'socialMediaLinks', index)
                                }
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-success btn-sm mt-2"
                        onClick={() => handleAddNestedDynamicField('creator', 'person', 'socialMediaLinks')}
                    >
                        Add Social Media Link
                    </button>
                </div>

               {/* Form Section Label */}
                <div className="inferable-form-section">
                    <h5>Creator (Organization)</h5>
                </div>

                {/* Creator (Organization) */}
                <div className="form-group">
                    <label htmlFor="organizationName" title="Enter an organization name." data-toggle="tooltip">Name</label>
                    <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        className="form-control"
                        value={formData.creator.sourceOrganization.name}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'sourceOrganization', {
                                ...formData.creator.sourceOrganization,
                                name: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="emailOrganization" title="Enter an email address." data-toggle="tooltip">Email</label>
                    <input
                        type="email"
                        id="emailOrganization"
                        name="emailOrganization"
                        className="form-control"
                        value={formData.creator.sourceOrganization.email}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'sourceOrganization', {
                                ...formData.creator.sourceOrganization,
                                email: e.target.value,
                            })
                        }
                    />
                    <label htmlFor="websiteOrganization" title="Enter a web site address." data-toggle="tooltip">Website</label>
                    <input
                        type="url"
                        id="websiteOrganization"
                        name="websiteOrganization"
                        className="form-control"
                        value={formData.creator.sourceOrganization.website}
                        onChange={(e) =>
                            handleNestedInputChange('creator', 'sourceOrganization', {
                                ...formData.creator.sourceOrganization,
                                website: e.target.value,
                            })
                        }
                    />

                    {/* Social Media Links for Organization */}
                    <label title="Enter 0 or more social media links." data-toggle="tooltip">Social Media Links (Organization)</label>
                    {formData.creator.sourceOrganization.socialMediaLinks.map((link, index) => (
                        <div key={index} className="d-flex align-items-center">
                            <input
                                type="url"
                                className="form-control mr-2"
                                value={link}
                                onChange={(e) =>
                                    handleNestedDynamicFieldChange(
                                        'creator',
                                        'sourceOrganization',
                                        'socialMediaLinks',
                                        index,
                                        e.target.value
                                    )
                                }
                            />
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() =>
                                    handleRemoveNestedDynamicField(
                                        'creator',
                                        'sourceOrganization',
                                        'socialMediaLinks',
                                        index
                                    )
                                }
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn btn-success btn-sm mt-2"
                        onClick={() => handleAddNestedDynamicField('creator', 'sourceOrganization', 'socialMediaLinks')}
                    >
                        Add Social Media Link
                    </button>
                </div>
                        <button type="submit" className="btn btn-primary mt-3">
                            Save Object
                        </button>
                </form>
            </div>
        </div>
        </>
    );
}

export default CrudUI;